import type { ResidenceSectionV2 } from "@credit-acceptance/step6-bff-client";

import { asRecord, readField, readNumber, readYesNo } from "./callScript.mapping";
import {
  EMPTY_RESIDENCE_VERIFIED_FIELDS,
  type ResidenceCallScriptEntry,
  type ResidenceCallScriptHistory,
  type ResidenceCallScriptHistoryAnswer,
  type ResidenceVerifiedFields,
} from "./callScriptResidence.types";

// ---------------------------------------------------------------------------
// callScriptResidence.mapping
//
// Maps the dashboard's ResidenceSectionV2 onto the Customer Verification Call
// Script's "Saved Information" column. Mirrors callScriptEmploymentMapping.ts
// (the employment equivalent) for the fields this section actually carries —
// the Contact block's date of birth/primary/secondary phone, and the
// Physical/Mailing Address blocks.
//
// The Deal/down-payment block (whoIsPrimaryDriver, tradeIn,
// totalCashAndTradeIn, anyAmountStillOwed, whenIsRemainingDue,
// grossTradeInAmount, tradeInPayoffAmount, cashDownPaidOnDelivery,
// amountDueAfterDelivery, whenIsFinalAmountDue) has no BFF/dashboard backing
// anywhere in this app — those fields are left at their empty defaults.
// ---------------------------------------------------------------------------

/**
 * Maps a saved `ResidenceSectionV2` into the modal's field shape, for
 * rendering the read-only "Saved Information" controls (same input types as
 * "Verified Information", disabled). The Deal/down-payment block has no BFF
 * backing and is left at its empty defaults — see the module comment.
 * @param {ResidenceSectionV2 | undefined} residence - The applicant's saved residence section, if any.
 * @returns {ResidenceVerifiedFields} The saved-fields state.
 */
export function mapResidenceToSavedFields(residence: ResidenceSectionV2 | undefined): ResidenceVerifiedFields {
  if (!residence) {
    return { ...EMPTY_RESIDENCE_VERIFIED_FIELDS };
  }
  const physical = residence.current;
  const mailing = residence.mailing;
  const phones = [...(residence.phones ?? [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

  return {
    ...EMPTY_RESIDENCE_VERIFIED_FIELDS,
    dateOfBirth: residence.dateOfBirth ?? "",
    primaryNumber: phones[0]?.number ?? "",
    secondaryNumber: phones[1]?.number ?? "",
    physicalStreetNumber: physical?.streetNumber ?? "",
    physicalStreetName: physical?.streetName ?? "",
    physicalApartmentNumber: physical?.apartmentNumber ?? "",
    physicalZip: physical?.zip ?? "",
    physicalCity: physical?.city ?? "",
    physicalState: physical?.state ?? "",
    mailingStreetNumber: mailing?.streetNumber ?? "",
    mailingStreetName: mailing?.streetName ?? "",
    mailingApartmentNumber: mailing?.apartmentNumber ?? "",
    mailingZip: mailing?.zip ?? "",
    mailingCity: mailing?.city ?? "",
    mailingState: mailing?.state ?? "",
  };
}

/**
 * Maps a raw residence history `answers` entry defensively, degrading
 * unknown/missing keys rather than throwing.
 * @param {unknown} answer - One raw answer record.
 * @returns {ResidenceCallScriptHistoryAnswer} The mapped answer.
 */
function mapResidenceHistoryAnswer(answer: unknown): ResidenceCallScriptHistoryAnswer {
  const row = asRecord(answer);
  return {
    verificationAnswerPk: readNumber(row, "verificationAnswerPk") ?? 0,
    verificationQuestionPk: readNumber(row, "verificationQuestionPk"),
    questionText: readField(row, "questionText"),
    answerEntered: readField(row, "answerEntered"),
    answerStored: readField(row, "answerStored"),
    isAnswerMatch: readYesNo(row, "isAnswerMatch"),
  };
}

/**
 * Maps a single raw residence history entry (recap's call record, forwarded
 * verbatim by `getResidenceHistory`) to the app's view model.
 * @param {unknown} entry - One raw history record.
 * @returns {ResidenceCallScriptEntry} The mapped entry.
 */
export function mapResidenceCallScriptEntry(entry: unknown): ResidenceCallScriptEntry {
  const row = asRecord(entry);
  return {
    verificationPhoneCallPk: readNumber(row, "verificationPhoneCallPk") ?? 0,
    verificationOutcome: readField(row, "verificationOutcome"),
    phoneNumberDialed: readField(row, "phoneNumberDialed"),
    occurredAt: readField(row, "occurredAt"),
    analystName: readField(row, "analystName"),
    applicantAddressPk: readNumber(row, "applicantAddressPk"),
    answers: Array.isArray(row.answers) ? row.answers.map((answer) => mapResidenceHistoryAnswer(answer)) : [],
  };
}

/**
 * Maps a residence Call Script history response into entries. The generated
 * client types `getResidenceHistory` as `void`, so — like the employment
 * `mapCallScriptHistory` — this accepts either a bare array of entries or an
 * object wrapping them under `entries` / `history` / `rows`; anything else
 * yields no entries.
 * @param {unknown} payload - The raw `getResidenceHistory` response.
 * @returns {ResidenceCallScriptHistory} The history view model.
 */
export function mapResidenceCallScriptHistory(payload: unknown): ResidenceCallScriptHistory {
  const record = asRecord(payload);
  const candidate = Array.isArray(payload) ? payload : (record.entries ?? record.history ?? record.rows);
  const entries = Array.isArray(candidate) ? candidate : [];
  return { entries: entries.map((entry) => mapResidenceCallScriptEntry(entry)) };
}
